import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AtellioFeatureGroupDefinition, featureFlags } from 'v5/features';
import { FeatureName } from 'v5/platform/feature-management/feature-management';
import { WithServices } from 'v5/platform/ts/services';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { FeatureVersionToggle } from '../useFeatureVersionToggle';
import { VersionSwitchPopover } from './VersionSwitchPopover';

export interface ToggleVersionSwitchProps
  extends WithServices<[WithToastService, WithSupportService]> {
  toggle: FeatureVersionToggle;
  /** Tags the feedback message for the support team. */
  featureName: FeatureName<AtellioFeatureGroupDefinition>;
  /** Also names the feature in the feedback message. */
  title: string;
  newVersionDescription: ReactNode;
  oldVersionDescription: ReactNode;
  newVersionPreview: ReactNode;
  oldVersionPreview: ReactNode;
}

/**
 * `VersionSwitchPopover` driven by a version toggle, with an undo toast after
 * each switch and feedback sent to support.
 */
export function ToggleVersionSwitch({
  services,
  toggle,
  featureName,
  title,
  newVersionDescription,
  oldVersionDescription,
  newVersionPreview,
  oldVersionPreview
}: ToggleVersionSwitchProps) {
  const { t } = useTranslation('common-widgets');

  return (
    <VersionSwitchPopover
      isNewVersion={toggle.isNewVersion}
      onSwitch={useNewVersion => {
        // This toast confirms the switch, with Undo.
        toggle.switchVersion(useNewVersion, { successToast: false });
        services.toastService.open({
          type: 'success',
          message: useNewVersion
            ? t('VersionSwitch.switchedToNew')
            : t('VersionSwitch.switchedToClassic'),
          action: {
            label: t('VersionSwitch.undo'),
            onClick: () =>
              toggle.switchVersion(!useNewVersion, { successToast: false })
          }
        });
      }}
      title={title}
      newVersionDescription={newVersionDescription}
      oldVersionDescription={oldVersionDescription}
      newVersionPreview={newVersionPreview}
      oldVersionPreview={oldVersionPreview}
      onSendFeedback={() => {
        // The tag stays English: it is for the support team, not the user.
        const flagName =
          featureFlags.groups
            .flatMap<{ id: string; name: string }>(group => group.entries)
            .find(entry => entry.id === featureName)?.name ?? featureName;
        const tag = `[New ${flagName}]`;
        const draft = t('VersionSwitch.feedbackDraft', {
          feature: title,
          // Plain text: the support service escapes it for the composer.
          interpolation: { escapeValue: false }
        });
        void services.supportService
          .showNewMessage(`${tag} ${draft}\n`)
          .catch(() => null);
      }}
    />
  );
}
